import React from 'react';
import { MdClose, MdEmail, MdWork, MdSchool, MdLink, MdLocationOn } from 'react-icons/md';
import { EnhancedUser } from 'types/profile.types';
import Avatar from 'components/avatar/Avatar';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: EnhancedUser | null;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const profile = user.profile;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            User Profile
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg transition-colors"
          >
            <MdClose className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Basic Info Section */}
          <div className="flex items-start gap-6 mb-8">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar 
                uid={user.uid} 
                displayName={user.displayName} 
                size="large"
                className="border-4 border-gray-200 dark:border-gray-600"
              />
            </div>

            {/* Basic Details */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                  {user.displayName}
                </h3>
                {profile?.mentor && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-300">
                    Mentor
                  </span>
                )}
                {(() => {
                  const locationParts = [
                    profile?.locationData?.city,
                    profile?.locationData?.state,
                    profile?.locationData?.country
                  ].filter(Boolean);
                  
                  return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      <MdLocationOn className="h-3 w-3" />
                      {locationParts.length > 0 ? locationParts.join(', ') : 'Planet Earth'}
                    </span>
                  );
                })()}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                <div className="flex items-center gap-1">
                  <MdEmail className="h-4 w-4" />
                  {profile?.email || user.authEmail}
                </div>
                <span>•</span>
                <span>
                  Created: {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                </span>
                <span>•</span>
                <span>
                  Last active: {user.lastActiveFormatted || 'N/A'}
                </span>
              </div>
              
              {/* Account Status Badge */}
              {user.disabled && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
                    Disabled
                  </span>
                </div>
              )}

              {profile?.quote && (
                <p className="text-gray-600 dark:text-gray-400 italic">
                  "{profile.quote}"
                </p>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


            {/* Education */}
            {profile?.educations && profile.educations.length > 0 && (
              <div className="bg-gray-50 dark:bg-navy-900 rounded-lg p-4">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                  <MdSchool className="h-4 w-4" />
                  Education
                </h4>
                <div className="space-y-3">
                  {profile.educations.slice(0, 2).map((edu, index) => (
                    <div key={index} className="text-sm">
                      <p className="font-medium text-navy-700 dark:text-white">
                        {edu.collegeName}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {edu.level} {edu.majors.length > 0 && `• ${edu.majors.join(', ')}`}
                      </p>
                    </div>
                  ))}
                  {profile.educations.length > 2 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      +{profile.educations.length - 2} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Work Experience */}
            {profile?.works && profile.works.length > 0 && (
              <div className="bg-gray-50 dark:bg-navy-900 rounded-lg p-4">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                  <MdWork className="h-4 w-4" />
                  Work Experience
                </h4>
                <div className="space-y-3">
                  {profile.works.slice(0, 2).map((work, index) => (
                    <div key={index} className="text-sm">
                      <p className="font-medium text-navy-700 dark:text-white">
                        {work.position}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        {work.companyName} {work.field.length > 0 && `• ${work.field.join(', ')}`}
                      </p>
                    </div>
                  ))}
                  {profile.works.length > 2 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      +{profile.works.length - 2} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Interests */}
            {profile?.interests && profile.interests.length > 0 && (
              <div className="bg-gray-50 dark:bg-navy-900 rounded-lg p-4">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3">
                  Interests
                </h4>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.slice(0, 10).map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                    >
                      {interest}
                    </span>
                  ))}
                  {profile.interests.length > 10 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      +{profile.interests.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Links */}
            {profile?.links && profile.links.length > 0 && (
              <div className="bg-gray-50 dark:bg-navy-900 rounded-lg p-4">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                  <MdLink className="h-4 w-4" />
                  Links
                </h4>
                <div className="space-y-2">
                  {profile.links.slice(0, 5).map((link, index) => (
                    <a
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline truncate"
                    >
                      {link}
                    </a>
                  ))}
                  {profile.links.length > 5 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      +{profile.links.length - 5} more links
                    </p>
                  )}
                </div>
              </div>
            )}


            {/* Statistics */}
            <div className="bg-gray-50 dark:bg-navy-900 rounded-lg p-4 md:col-span-2">
              <h4 className="font-semibold text-navy-700 dark:text-white mb-3">
                Statistics
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-500">{profile?.matches?.length || 0}</p>
                  <p className="text-gray-600 dark:text-gray-400">Matches</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-500">{profile?.meetings?.length || 0}</p>
                  <p className="text-gray-600 dark:text-gray-400">Meetings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-500">{(profile?.acceptedCards?.length || 0) + (profile?.rejectedCards?.length || 0)}</p>
                  <p className="text-gray-600 dark:text-gray-400">Modules Engaged</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-500">{user.privateData?.mentees?.length || 0}</p>
                  <p className="text-gray-600 dark:text-gray-400">Mentees</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-teal-500">{user.privateData?.mentors?.length || 0}</p>
                  <p className="text-gray-600 dark:text-gray-400">Mentors</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;